import React from 'react';
import { I18n } from '@fiverr-private/i18n-react';
import { Gql } from '@fiverr-private/dynamic_matching';
import {
  BudgetRange,
  getBudget,
  getBudgetRange,
  getOriginalBudget,
  getOriginalBudgetRange,
} from '../../utils/formatCurrency';
import type { SellerBriefResponse } from '../../../types/SellerBriefResponse';
import { BudgetAndTimelineItem } from './BudgetAndTimelineItem';

const T_PREFIX = 'brief_preview_drawer.person_brief.budget';

export const FixedPrice = ({
  budgetInCents,
  isOngoing,
  originalBuyerBudget,
  scope,
}: {
  budgetInCents: SellerBriefResponse['budgetInCents'];
  isOngoing?: boolean;
  originalBuyerBudget: SellerBriefResponse['originalBuyerBudget'];
  scope: 'seller' | 'buyer';
}) => {
  const fixedPriceText = isOngoing ? 'per_delivery' : 'value';
  const price: string =
    scope === 'seller' ? getBudget(budgetInCents) : getOriginalBudget(budgetInCents, originalBuyerBudget);

  return (
    <BudgetAndTimelineItem type="budget" title="fixed_price" iconName="budget">
      <I18n k={`${T_PREFIX}.fixed_price.${fixedPriceText}`} params={{ price }} />
    </BudgetAndTimelineItem>
  );
};

export const HourlyRate = ({
  hourlyBudgetRange,
  originalBuyerBudgetRange,
  budgetInCents,
  originalBuyerBudget,
  scope,
}: {
  hourlyBudgetRange: SellerBriefResponse['budgetRangeInCents'];
  originalBuyerBudgetRange: SellerBriefResponse['originalBuyerBudgetRange'];
  budgetInCents: SellerBriefResponse['budgetInCents'];
  originalBuyerBudget: SellerBriefResponse['originalBuyerBudget'];
  scope: 'seller' | 'buyer';
}) => {
  let params: { min?: string; value?: string } = {};

  if (scope === 'buyer') {
    params = originalBuyerBudgetRange
      ? getOriginalBudgetRange(hourlyBudgetRange as BudgetRange, originalBuyerBudgetRange)
      : { value: getOriginalBudget(budgetInCents, originalBuyerBudget) };
  } else {
    params = hourlyBudgetRange ? getBudgetRange(hourlyBudgetRange) : { value: getBudget(budgetInCents) };
  }

  return (
    <BudgetAndTimelineItem type="budget" title="hourly_rates" iconName="budget">
      <I18n k={`${T_PREFIX}.hourly_rates.${params.min ? 'range' : 'fixed_rate'}`} params={params} />
    </BudgetAndTimelineItem>
  );
};

export const Budget = ({
  budgetInCents,
  budgetRangeInCents,
  needType,
  pricingType,
  originalBuyerBudget,
  originalBuyerBudgetRange,
  scope,
}: {
  budgetInCents: SellerBriefResponse['budgetInCents'];
  originalBuyerBudget: SellerBriefResponse['originalBuyerBudget'];
  budgetRangeInCents: SellerBriefResponse['budgetRangeInCents'];
  originalBuyerBudgetRange: SellerBriefResponse['originalBuyerBudgetRange'];
  needType: SellerBriefResponse['needType'];
  pricingType: SellerBriefResponse['pricingType'];
  scope: 'seller' | 'buyer';
}) =>
  pricingType === Gql.BriefPricingType.Hourly ? (
    <HourlyRate
      budgetInCents={budgetInCents}
      hourlyBudgetRange={budgetRangeInCents}
      originalBuyerBudget={originalBuyerBudget}
      originalBuyerBudgetRange={originalBuyerBudgetRange}
      scope={scope}
    />
  ) : (
    <FixedPrice
      budgetInCents={budgetInCents}
      isOngoing={needType === Gql.DmBriefNeedType.Ongoing}
      originalBuyerBudget={originalBuyerBudget}
      scope={scope}
    />
  );
